import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import styles from "./Btn.module.css";

type Variant = "primary" | "secondary" | "ghost" | "destructive" | "subtle";
type Size = "sm" | "md" | "lg";

interface CommonProps {
  variant?: Variant;
  size?: Size;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  loading?: boolean;
  /** Stretch to fill the parent's width. Used in form CTAs. */
  full?: boolean;
  children?: ReactNode;
  className?: string;
}

type BtnAsButton = CommonProps & { href?: undefined } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof CommonProps>;
type BtnAsLink = CommonProps & { href: string } & { onClick?: never };

export type BtnProps = BtnAsButton | BtnAsLink;

function classes(variant: Variant, size: Size, full?: boolean, extra?: string): string {
  const parts = [styles.btn, styles[`v_${variant}`], styles[`s_${size}`]];
  if (full) parts.push(styles.full);
  if (extra) parts.push(extra);
  return parts.filter(Boolean).join(" ");
}

export const Btn = forwardRef<HTMLButtonElement | HTMLAnchorElement, BtnProps>(
  function Btn(props, ref) {
    const {
      variant = "primary",
      size = "md",
      iconLeft,
      iconRight,
      loading,
      full,
      children,
      className,
      ...rest
    } = props;

    const body = (
      <>
        {iconLeft && <span className={styles.icon}>{iconLeft}</span>}
        <span className={styles.label}>{children}</span>
        {iconRight && <span className={styles.icon}>{iconRight}</span>}
        {loading && <span className={styles.spinner} aria-hidden="true" />}
      </>
    );

    if ("href" in rest && rest.href) {
      const { href, ...anchorRest } = rest as { href: string };
      // External links open in a new tab; same-page hash links use a native
      // anchor so the browser scrolls to the target (react-router's Link only
      // updates the hash without scrolling); mailto:/tel: must be native
      // anchors too (handed to the OS, NOT routed — a Link would treat them as
      // an internal path and dead-click); everything else uses react-router.
      const isExternal = /^https?:\/\//i.test(href);
      const isHash = href.startsWith("#");
      const isProtocol = /^(mailto|tel):/i.test(href);
      if (isExternal || isHash || isProtocol) {
        return (
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            className={classes(variant, size, full, className)}
            href={href}
            {...(isExternal ? { rel: "noopener noreferrer", target: "_blank" } : {})}
            {...anchorRest}
          >
            {body}
          </a>
        );
      }
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={classes(variant, size, full, className)}
          to={href}
          {...anchorRest}
        >
          {body}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classes(variant, size, full, className)}
        disabled={loading || (rest as ButtonHTMLAttributes<HTMLButtonElement>).disabled}
        {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {body}
      </button>
    );
  },
);
